import React, { useRef, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import heroVideo from "../../../assets/videos/hero-home-video.mp4";

/**
 * HeroBanner component for the homepage
 *
 * @returns {React.Component} HeroBanner component
 */
const HeroBanner = () => {
    const videoRef = useRef(null);
    const theme = useTheme();

    // Use useEffect to ensure video plays correctly after component mounts
    useEffect(() => {
        if (videoRef.current) {
            // Ensure video loads properly
            videoRef.current.load();

            // Play video after load - important for some browsers
            const playPromise = videoRef.current.play();

            // Handle play promise to avoid errors
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.log('Video play error:', error);
                });
            }
        }
    }, []);

    return (
        <Box
            sx={{
                position: "relative", // Can remain relative if Layout handles full page
                height: "100vh",
                width: "100%", // Should be 100% of its container, which should be 100vw
                display: "flex",
                alignItems: { xs: "center", md: "flex-start" },
                justifyContent: { xs: "center", md: "flex-start" },
                paddingTop: { xs: "72px", sm: "26vh", md: "30vh" },
                color: theme.palette.common.white,
                overflow: "hidden", // Important to prevent scrollbars from video
            }}
        >
            {/* Video Background */}
            <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline // Important for iOS
                style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    left: "50%",
                    top: "50%",
                    objectFit: "cover",
                    transform: "translate(-50%, -50%)",
                    zIndex: 0, // Behind overlay and text
                }}
            >
                <source src={heroVideo} type="video/mp4" />
                Your browser does not support the video tag.
            </video>

            {/* Overlay */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.15)",
                    zIndex: 1,
                }}
            />

            {/* Text Content */}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 2,
                    padding: { 
                        xs: theme.spacing(0, 2), 
                        sm: theme.spacing(0, 4), 
                        md: theme.spacing(0, 6), 
                        lg: theme.spacing(0, 8) 
                    },
                    width: { xs: "100%", sm: "96%", md: "90%", lg: "80%" },
                    maxWidth: "1200px", 
                    textAlign: { xs: "center", sm: "left" },
                }}
            >
                <Typography
                    variant="h1"
                    sx={{
                        fontSize: {
                            xs: "1.8rem",
                            sm: "2.4rem",
                            md: "3rem",
                            lg: "3.5rem"
                        },
                        fontWeight: 400,
                        marginBottom: theme.spacing(2),
                        textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                        fontFamily: theme.typography.fontFamily,
                        lineHeight: { xs: 1.2, md: 1.1 },
                    }}
                >
                    The engine behind<br />
                    institutional-grade automation
                </Typography>
            </Box>
        </Box>
    );
};

export default HeroBanner;